import { MIN_BET, PAIR_MULT, SYMBOLS } from "../../../shared/slots.js";
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { drawCard, drawSceneBackground, drawSceneTitle, fillRoundedRect } from "../../utils/draw";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import { MenuScene } from "./MenuScene";
import type { GameContext, Scene } from "./Scene";

const REEL_W = 84;
const REEL_H = 100;
const REEL_Y = 150;
const REEL_GAP = 12;
const REEL_X0 = (VIRTUAL_WIDTH - (3 * REEL_W + 2 * REEL_GAP)) / 2;
const BETS = [5, 25, 100, 500];
const CHIP_Y = 320;
const CHIP_W = 70;
const CHIP_GAP = 8;
const CHIP_X0 = (VIRTUAL_WIDTH - (BETS.length * CHIP_W + (BETS.length - 1) * CHIP_GAP)) / 2;
const SPIN_BTN = { x: VIRTUAL_WIDTH / 2 - 90, y: 366, w: 180, h: 48 };
// Reels stop one after another for a slot-machine feel.
const REEL_STOP = [0.7, 1.0, 1.3];

export class SlotScene implements Scene {
  private displayed = [0, 1, 2];
  private spinning = false;
  private result: number[] | null = null;
  private spinT = 0;
  private cycleAccum = 0;
  private bet = BETS[1]!;
  private msg = "";
  private msgColor = "#f5e9c8";
  private msgT = 0;
  private entered = false;

  constructor(private readonly ctxMgr: GameContext) {}

  private get wallet(): number {
    return this.ctxMgr.shop.wallet;
  }
  private get canSpin(): boolean {
    return !this.spinning && this.bet >= MIN_BET && this.bet <= this.wallet;
  }

  enter(): void {
    this.entered = true;
    this.rebuild();
  }

  exit(): void {
    this.entered = false;
    this.ctxMgr.buttons.clear();
  }

  private rebuild(): void {
    const buttons: Button[] = [backButton(() => this.ctxMgr.goTo(new MenuScene(this.ctxMgr)))];
    BETS.forEach((amount, i) => {
      const r = { x: CHIP_X0 + i * (CHIP_W + CHIP_GAP), y: CHIP_Y, w: CHIP_W, h: 30 };
      buttons.push({
        ...r,
        draw: (ctx, hit) => this.drawChip(ctx, amount, r, hit),
        onPress: () => {
          if (this.spinning) return;
          this.bet = amount;
        },
      });
    });
    buttons.push({
      ...SPIN_BTN,
      draw: (ctx, hit) => this.drawSpinButton(ctx, hit),
      onPress: () => void this.doSpin(),
    });
    this.ctxMgr.buttons.set(buttons);
  }

  private async doSpin(): Promise<void> {
    if (!this.canSpin) return;
    this.spinning = true;
    this.result = null;
    this.spinT = 0;
    this.cycleAccum = 0;
    this.msg = "";
    this.ctxMgr.audio.play("flap");
    const res = await this.ctxMgr.slots.spin(
      this.ctxMgr.settings.playerName,
      this.ctxMgr.settings.authToken,
      this.bet,
    );
    if (!this.entered) return;
    if ("error" in res) {
      this.spinning = false;
      this.msg = res.error === "insufficient_flies" ? "Not enough flies!" : "Spin failed.";
      this.msgColor = "#ff8a8a";
      this.msgT = 2.5;
      return;
    }
    // Reels keep cycling in update() until each one's stop time, then lock to result.
    this.result = res.reels;
    this.pendingPayout = res.payout;
  }

  private pendingPayout = 0;

  update(dt: number): void {
    if (this.msgT > 0) this.msgT -= dt;
    if (!this.spinning) return;
    this.spinT += dt;
    this.cycleAccum += dt;
    const cycling = this.cycleAccum >= 0.06;
    if (cycling) this.cycleAccum = 0;
    let allStopped = this.result !== null;
    for (let i = 0; i < 3; i += 1) {
      const stopped = this.result !== null && this.spinT >= REEL_STOP[i]!;
      if (stopped) {
        this.displayed[i] = this.result![i]!;
      } else {
        allStopped = false;
        if (cycling) this.displayed[i] = Math.floor(Math.random() * SYMBOLS.length);
      }
    }
    if (allStopped) this.finishSpin();
  }

  private finishSpin(): void {
    this.spinning = false;
    const payout = this.pendingPayout;
    if (payout > 0) {
      const triple = this.result![0] === this.result![1] && this.result![1] === this.result![2];
      this.msg = triple ? `JACKPOT!  +${payout} 🪰` : `Win!  +${payout} 🪰`;
      this.msgColor = "#9fe6a0";
      this.ctxMgr.audio.play("score");
    } else {
      this.msg = "No win — spin again!";
      this.msgColor = "#9fb0c8";
      this.ctxMgr.audio.play("hit");
    }
    this.msgT = 3;
    // Sync the authoritative balance (bet debited + any payout credited).
    void this.ctxMgr.shop.refresh();
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);
    drawSceneTitle(ctx, "SLOTS");

    ctx.textAlign = "center";
    if (!this.ctxMgr.settings.isLoggedIn()) {
      ctx.fillStyle = "#f5e9c8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("Log in to play the slots.", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2);
      return;
    }

    ctx.fillStyle = "#f0d98a";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.fillText(`${this.wallet} 🪰`, VIRTUAL_WIDTH / 2, 78);

    // Reels.
    for (let i = 0; i < 3; i += 1) {
      const x = REEL_X0 + i * (REEL_W + REEL_GAP);
      drawCard(ctx, x, REEL_Y, REEL_W, REEL_H, 12, { fill: "#0e1118", border: "#7a5e2a" });
      ctx.font = "46px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(SYMBOLS[this.displayed[i]!]!.emoji, x + REEL_W / 2, REEL_Y + REEL_H / 2 + 2);
      ctx.textBaseline = "alphabetic";
    }

    // Win / status message.
    if (this.msgT > 0 || this.spinning) {
      ctx.fillStyle = this.spinning ? "#9fb0c8" : this.msgColor;
      ctx.font = "bold 16px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(this.spinning ? "Spinning…" : this.msg, VIRTUAL_WIDTH / 2, 285);
    }

    // Paytable: 3-of-a-kind multipliers + the pair payout.
    ctx.textAlign = "center";
    ctx.fillStyle = "#8a93a8";
    ctx.font = "12px system-ui, sans-serif";
    const pay = SYMBOLS.map((s) => `${s.emoji}×${s.three}`);
    ctx.fillText(pay.slice(0, 3).join("   "), VIRTUAL_WIDTH / 2, 446);
    ctx.fillText(pay.slice(3).join("   "), VIRTUAL_WIDTH / 2, 470);
    ctx.fillText(`any pair ×${PAIR_MULT}`, VIRTUAL_WIDTH / 2, 496);
  }

  private drawChip(
    ctx: CanvasRenderingContext2D,
    amount: number,
    r: { x: number; y: number; w: number; h: number },
    hit: boolean,
  ): void {
    const selected = this.bet === amount;
    ctx.fillStyle = selected ? "#f0d98a" : hit ? "#3a4256" : "#222838";
    fillRoundedRect(ctx, r.x, r.y, r.w, r.h, 8);
    ctx.fillStyle = selected ? "#1a1f2b" : "#dbe2f0";
    ctx.font = "bold 13px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(amount), r.x + r.w / 2, r.y + r.h / 2);
    ctx.textBaseline = "alphabetic";
  }

  private drawSpinButton(ctx: CanvasRenderingContext2D, hit: boolean): void {
    const enabled = this.canSpin;
    ctx.fillStyle = !enabled ? "#2a3140" : hit ? "#1b7a3a" : "#2e7d32";
    fillRoundedRect(ctx, SPIN_BTN.x, SPIN_BTN.y, SPIN_BTN.w, SPIN_BTN.h, 12);
    ctx.fillStyle = enabled ? "#fff" : "#6a7280";
    ctx.font = "bold 18px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const label = this.spinning ? "Spinning…" : this.bet > this.wallet ? "Not enough flies" : `SPIN  ·  ${this.bet} 🪰`;
    ctx.fillText(label, SPIN_BTN.x + SPIN_BTN.w / 2, SPIN_BTN.y + SPIN_BTN.h / 2);
    ctx.textBaseline = "alphabetic";
  }
}
